import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  CustomersCustomerSubscriptionsRequest,
  CustomersDeleteParams,
  CustomersExportCustomersRequest,
  CustomersRequestBody,
} from '@customerio/hydra-ui-api';
import { hydraUiApi } from '@/services/api';
import type { HookMutationConfig } from '../shared/react-query-types';
import { requireWorkspaceId } from '../shared/workspace';
import { environmentKeys, type WorkspaceEnvironment } from '../environment/entity';
import { customerKeys, type Customer } from './entity';

/**
 * Customer write actions.
 *
 * The onboarding/sandbox flows use the two single-profile actions
 * (`createCustomerProfile`, `markCustomerAsTestUser`). The People-list bulk
 * actions (`deleteCustomers`, `unsubscribeCustomers`, `markCustomersAsVerified`)
 * operate on an explicit set of selected profiles.
 *
 * SCOPE: the "select all across pages" delete branch sends the decoded
 * `filters` condition tree as-is (`type: 'filters'`) — the modern object wire
 * is already the structured `Filter` the endpoint wants. Legacy bare-array
 * deep-link filters have no serializer in Hydra (dryad-first decision), so
 * callers disable filter-based delete for that shape rather than build the
 * payload here.
 *
 * Path parameters are encoded by the generated client, so callers pass the
 * raw external identifier (e.g. an email) without pre-encoding it.
 */

/** The attribute map of the generated create/update request body. */
type CustomerAttributesBody = CustomersRequestBody['customer']['attributes'];

export interface CreateCustomerInput {
  workspaceId: number;
  /** The profile's attributes; an empty string removes (or never sets) that attribute. */
  attributes: CustomerAttributesBody;
}

/**
 * Queue a new profile. The backend answers 202 and creates the profile through
 * its ingress pipeline, so it may not be queryable right away — callers that
 * need the created record poll for it (see the add-person flow).
 *
 * @public
 */
export async function createCustomer({ workspaceId, attributes }: CreateCustomerInput): Promise<void> {
  await hydraUiApi.customers.create({
    params: { path: { environment_id: String(workspaceId) } },
    body: { customer: { attributes } },
  });
}

/** @public Create a profile carrying the given email attribute. */
export async function createCustomerProfile(workspaceId: number, email: string): Promise<void> {
  await createCustomer({ workspaceId, attributes: { email } });
}

/**
 * The environment aggregate with `sent_attributes` extended by any names it did
 * not list yet, so attribute pickers offer a profile's new attributes before the
 * backend has processed the create.
 *
 * @public
 */
export function withSentAttributes(
  environment: WorkspaceEnvironment,
  attributeNames: readonly string[],
): WorkspaceEnvironment {
  const record = environment.environment;
  if (record == null) {
    return environment;
  }
  const sentAttributes = [...record.sent_attributes];
  for (const name of attributeNames) {
    if (!sentAttributes.includes(name)) {
      sentAttributes.push(name);
    }
  }
  return { ...environment, environment: { ...record, sent_attributes: sentAttributes } };
}

/**
 * The attribute names a create actually sends. An empty value means "remove
 * this attribute", so a blanked name is not one the workspace has received and
 * does not belong in the attribute catalog.
 *
 * @public
 */
export function sentAttributeNames(attributes: CustomerAttributesBody): string[] {
  return Object.entries(attributes)
    .filter(([, value]) => value !== '')
    .map(([name]) => name);
}

/**
 * Send the double-opt-in email that flags a profile as a test user.
 * `identifier` is the customer's external id (the email on Builder-plan,
 * email-identified workspaces).
 *
 * @public
 */
export async function markCustomerAsTestUser(workspaceId: number, identifier: string): Promise<void> {
  await hydraUiApi.customers.doubleOptIn({
    params: { path: { environment_id: String(workspaceId), customer_id: identifier } },
  });
}

/* ------------------------------------------------------------------------- */
/* Bulk actions (selected-rows)                                               */
/* ------------------------------------------------------------------------- */

/**
 * Bulk delete. The body is the generated `CustomersDeleteParams` (the `type`
 * field selects the match strategy: `internal_ids` / `email` / `filters` /
 * `customer_ids`, with `suppress` controlling the suppression list) — derived
 * from the spec, not re-declared. Filter-based select-all is supported by the
 * endpoint via `type: 'filters'`; callers build the body directly.
 */
export interface DeleteCustomersInput extends CustomersDeleteParams {
  workspaceId: number;
}

/** @public */
export async function deleteCustomers({ workspaceId, ...body }: DeleteCustomersInput): Promise<void> {
  await hydraUiApi.customers.delete({
    params: { path: { environment_id: String(workspaceId) } },
    body,
  });
}

/**
 * Bulk unsubscribe. Body is the generated `CustomersCustomerSubscriptionsRequest`
 * (requires both `internal_ids` and `customer_ids`) — Ember only used
 * `internal_ids`, so callers pass an empty `customer_ids`.
 */
export interface UnsubscribeCustomersInput extends CustomersCustomerSubscriptionsRequest {
  workspaceId: number;
}

/** @public */
export async function unsubscribeCustomers({ workspaceId, ...body }: UnsubscribeCustomersInput): Promise<void> {
  await hydraUiApi.customers.unsubscribe({
    params: { path: { environment_id: String(workspaceId) } },
    body,
  });
}

export interface MarkCustomersAsVerifiedInput {
  workspaceId: number;
  customerIds: string[];
}

export interface MarkCustomersAsVerifiedResult {
  succeeded: number;
  failed: number;
}

/**
 * Mark each selected profile as verified via per-id double-opt-in. Each PUT is
 * independent (`Promise.allSettled`) so one failure doesn't abort the batch —
 * mirroring the Ember `ignoreErrors: true` per-request behavior. Callers should
 * pre-filter profiles without an email / already-confirmed before calling.
 *
 * @public
 */
export async function markCustomersAsVerified(
  input: MarkCustomersAsVerifiedInput,
): Promise<MarkCustomersAsVerifiedResult> {
  const results = await Promise.allSettled(
    input.customerIds.map((id) => markCustomerAsTestUser(input.workspaceId, id)),
  );
  const failed = results.filter((r) => r.status === 'rejected').length;
  return { succeeded: results.length - failed, failed };
}

/**
 * Wire values for the per-contact `cio_email_tracking_consent` attribute.
 * Reads tolerate boolean spellings synced from CDPs, but writes always use
 * the canonical lowercase forms the send path checks.
 */
export type EmailTrackingConsentValue = 'granted' | 'denied';

export const EMAIL_TRACKING_CONSENT_ATTRIBUTE = 'cio_email_tracking_consent';

export interface SetEmailTrackingConsentInput {
  workspaceId: number;
  /**
   * Each profile's id plus its current attributes — the customer update PUT
   * replaces the whole attribute map, so the consent key is merged into it.
   */
  profiles: ReadonlyArray<Pick<Customer, 'id' | 'attributes'>>;
  consent: EmailTrackingConsentValue;
}

export interface BulkActionTally {
  total: number;
  failed: number;
}

/**
 * Set the per-contact email tracking consent attribute on each profile, one
 * PUT per profile. Each request is independent (`Promise.allSettled`) so one
 * failure doesn't abort the batch; callers report the partial-failure tally.
 *
 * @public
 */
export async function setCustomersEmailTrackingConsent({
  workspaceId,
  profiles,
  consent,
}: SetEmailTrackingConsentInput): Promise<BulkActionTally> {
  const results = await Promise.allSettled(
    profiles.map((profile) =>
      hydraUiApi.customers.update({
        params: { path: { environment_id: String(workspaceId), customer_id: profile.id } },
        body: {
          customer: { attributes: { ...profile.attributes, [EMAIL_TRACKING_CONSENT_ATTRIBUTE]: consent } },
        },
      }),
    ),
  );
  const failed = results.filter((r) => r.status === 'rejected').length;
  return { total: results.length, failed };
}

/* ------------------------------------------------------------------------- */
/* Bulk-action hooks                                                          */
/* ------------------------------------------------------------------------- */

/**
 * A bulk op changes list membership, the filtered/unfiltered counts, and the
 * test-user set, so invalidate the whole workspace customer subtree.
 */
function useInvalidateCustomerData(workspaceId: number) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: customerKeys.all(workspaceId) });
  };
}

type DeleteVariables = Omit<DeleteCustomersInput, 'workspaceId'>;

/** @public */
export const useDeleteCustomers = ({
  workspaceId: overrideWorkspaceId,
  mutationConfig,
}: { workspaceId?: number; mutationConfig?: HookMutationConfig<void, DeleteVariables> } = {}) => {
  const workspaceId = overrideWorkspaceId ?? requireWorkspaceId();
  const invalidate = useInvalidateCustomerData(workspaceId);
  const { onSuccess, ...rest } = mutationConfig ?? {};
  return useMutation({
    ...rest,
    mutationFn: (variables: DeleteVariables) => deleteCustomers({ workspaceId, ...variables }),
    onSuccess: (result, variables, ...callbackArgs) => {
      invalidate();
      onSuccess?.(result, variables, ...callbackArgs);
    },
  });
};

type UnsubscribeVariables = Omit<UnsubscribeCustomersInput, 'workspaceId'>;

/** @public */
export const useUnsubscribeCustomers = ({
  workspaceId: overrideWorkspaceId,
  mutationConfig,
}: { workspaceId?: number; mutationConfig?: HookMutationConfig<void, UnsubscribeVariables> } = {}) => {
  const workspaceId = overrideWorkspaceId ?? requireWorkspaceId();
  const invalidate = useInvalidateCustomerData(workspaceId);
  const { onSuccess, ...rest } = mutationConfig ?? {};
  return useMutation({
    ...rest,
    mutationFn: (variables: UnsubscribeVariables) => unsubscribeCustomers({ workspaceId, ...variables }),
    onSuccess: (result, variables, ...callbackArgs) => {
      invalidate();
      onSuccess?.(result, variables, ...callbackArgs);
    },
  });
};

type MarkVerifiedVariables = { customerIds: string[] };

/** @public */
export const useMarkCustomersAsVerified = ({
  workspaceId: overrideWorkspaceId,
  mutationConfig,
}: {
  workspaceId?: number;
  mutationConfig?: HookMutationConfig<MarkCustomersAsVerifiedResult, MarkVerifiedVariables>;
} = {}) => {
  const workspaceId = overrideWorkspaceId ?? requireWorkspaceId();
  const invalidate = useInvalidateCustomerData(workspaceId);
  const { onSuccess, ...rest } = mutationConfig ?? {};
  return useMutation({
    ...rest,
    mutationFn: (variables: MarkVerifiedVariables) => markCustomersAsVerified({ workspaceId, ...variables }),
    onSuccess: (result, variables, ...callbackArgs) => {
      invalidate();
      onSuccess?.(result, variables, ...callbackArgs);
    },
  });
};

type SetConsentVariables = Omit<SetEmailTrackingConsentInput, 'workspaceId'>;

/**
 * No automatic cache invalidation: the mutation resolves even when every PUT
 * failed (the tally reports it), so the caller decides — the People list
 * refetches only when the modal confirms, matching the page's reload-on-confirm
 * behavior.
 *
 * @public
 */
export const useSetCustomersEmailTrackingConsent = ({
  workspaceId: overrideWorkspaceId,
  mutationConfig,
}: {
  workspaceId?: number;
  mutationConfig?: HookMutationConfig<BulkActionTally, SetConsentVariables>;
} = {}) => {
  const workspaceId = overrideWorkspaceId ?? requireWorkspaceId();
  return useMutation({
    ...mutationConfig,
    mutationFn: (variables: SetConsentVariables) => setCustomersEmailTrackingConsent({ workspaceId, ...variables }),
  });
};

type CreateCustomerVariables = Omit<CreateCustomerInput, 'workspaceId'>;

/**
 * Create a profile. On success the cached environment's `sent_attributes`
 * gains the profile's attribute names (see {@link withSentAttributes}) and the
 * workspace's customer reads are invalidated, since a create adds a row.
 *
 * @public
 */
export const useCreateCustomer = ({
  workspaceId: overrideWorkspaceId,
  mutationConfig,
}: { workspaceId?: number; mutationConfig?: HookMutationConfig<void, CreateCustomerVariables> } = {}) => {
  const workspaceId = overrideWorkspaceId ?? requireWorkspaceId();
  const qc = useQueryClient();
  const invalidate = useInvalidateCustomerData(workspaceId);
  const { onSuccess, ...rest } = mutationConfig ?? {};
  return useMutation({
    ...rest,
    mutationFn: (variables: CreateCustomerVariables) => createCustomer({ workspaceId, ...variables }),
    onSuccess: (result, variables, ...callbackArgs) => {
      qc.setQueryData<WorkspaceEnvironment>(environmentKeys.current(workspaceId), (previous) =>
        previous == null ? previous : withSentAttributes(previous, sentAttributeNames(variables.attributes)),
      );
      invalidate();
      onSuccess?.(result, variables, ...callbackArgs);
    },
  });
};

/* ------------------------------------------------------------------------- */
/* Person-detail actions (single profile)                                     */
/* ------------------------------------------------------------------------- */

// `suppress` is derived from the generated delete params (not re-declared) so it
// can't drift from the spec; `cioId` is a domain arg wrapping the internal_ids body.
export type DeletePersonInput = Pick<CustomersDeleteParams, 'suppress'> & {
  workspaceId: number;
  /** The profile's internal `cio_id` (person mutations key off it specifically). */
  cioId: string;
};

/**
 * Delete a single profile. Thin wrapper over the bulk `deleteCustomers`
 * (`type: 'internal_ids'`) for the person-detail "Delete person" action.
 *
 * Uses the bulk endpoint deliberately — not the dedicated
 * `DELETE /customers/:customer_id` — because only the bulk endpoint carries
 * `suppress` (the dedicated one takes no query/body params). The person-detail
 * delete has a "delete and suppress" toggle, so `suppress` is required; this
 * mirrors Ember (`app/pods/person/controller.ts` posts to `customers/delete`
 * with `type: 'internal_ids'` + `suppress`).
 *
 * The backend deletes asynchronously (204, no content) — the profile
 * disappears eventually, not synchronously.
 *
 * @public
 */
export async function deletePerson({ workspaceId, cioId, suppress }: DeletePersonInput): Promise<void> {
  await deleteCustomers({ workspaceId, type: 'internal_ids', internal_ids: [cioId], suppress });
}

type DeletePersonVariables = Omit<DeletePersonInput, 'workspaceId'>;

/** @public */
export const useDeletePerson = ({
  workspaceId: overrideWorkspaceId,
  mutationConfig,
}: { workspaceId?: number; mutationConfig?: HookMutationConfig<void, DeletePersonVariables> } = {}) => {
  const workspaceId = overrideWorkspaceId ?? requireWorkspaceId();
  const invalidate = useInvalidateCustomerData(workspaceId);
  const { onSuccess, ...rest } = mutationConfig ?? {};
  return useMutation({
    ...rest,
    mutationFn: (variables: DeletePersonVariables) => deletePerson({ workspaceId, ...variables }),
    onSuccess: (result, variables, ...callbackArgs) => {
      invalidate();
      onSuccess?.(result, variables, ...callbackArgs);
    },
  });
};

// The request body is the spec body (`ProfileDataBody`) itself, plus the
// workspace path param — its fields are optional (services#23404), so callers
// send only the id they have (Ember posts just `internal_id`) and never
// fabricate `exported_from`.
type ProfileDataBody = NonNullable<Parameters<typeof hydraUiApi.exports.profileData>[0]>['body'];

export type ExportProfileDataInput = ProfileDataBody & { workspaceId: number };

/**
 * Kick off a full profile-data export for a single profile. Returns the created
 * export id (the backend serializes it as a string); the export itself is
 * produced asynchronously.
 *
 * @public
 */
export async function exportProfileData({ workspaceId, ...body }: ExportProfileDataInput) {
  return hydraUiApi.exports.profileData({
    params: { path: { environment_id: String(workspaceId) } },
    body,
  });
}

type ExportProfileDataVariables = Omit<ExportProfileDataInput, 'workspaceId'>;

/** @public */
export const useExportProfileData = ({
  workspaceId: overrideWorkspaceId,
  mutationConfig,
}: {
  workspaceId?: number;
  mutationConfig?: HookMutationConfig<Awaited<ReturnType<typeof exportProfileData>>, ExportProfileDataVariables>;
} = {}) => {
  const workspaceId = overrideWorkspaceId ?? requireWorkspaceId();
  const { onSuccess, ...rest } = mutationConfig ?? {};
  return useMutation({
    ...rest,
    mutationFn: (variables: ExportProfileDataVariables) => exportProfileData({ workspaceId, ...variables }),
    onSuccess,
  });
};

export interface ExportCustomersInput {
  workspaceId: number;
  /** The active email search term, or null when none. */
  email: string | null;
  /** The encoded (Base64) condition filters, or null when none. */
  filters: string | null;
  /** People attributes to include; null exports every attribute. */
  attributes: string[] | null;
  /**
   * Object attributes to include per object type id. When set, the backend
   * produces a single combined people+objects CSV; `'*'` selects all of a
   * type's attributes.
   */
  relationship_object_attributes?: Record<string, string[]>;
}

/**
 * Queue a CSV export of the people list. Returns the queued export id(s); the
 * export itself is produced asynchronously and polled via the `export` entity.
 *
 * @public
 */
export async function exportCustomers({ workspaceId, ...input }: ExportCustomersInput) {
  const body: CustomersExportCustomersRequest = {
    email: input.email,
    filters: input.filters,
    attributes: input.attributes,
    exported_from: 'people_list',
    export_relationships: input.relationship_object_attributes != null,
  };
  if (input.relationship_object_attributes != null) {
    body.relationship_object_attributes = input.relationship_object_attributes;
  }
  return hydraUiApi.customers.exports({
    params: { path: { environment_id: String(workspaceId) } },
    body,
  });
}
